import { useQueries, useQuery } from '@tanstack/react-query';

import type { CanvasCapture, Persona } from '@repo/contracts';

import {
  canvasKeys,
  listProjectCaptures,
  listRooms,
} from '../api/canvas-captures';
import { listPersonas, personaKeys } from '../api/canvas-personas';

/**
 * Home's data: every room, newest first, plus the captures and panel faces
 * their cards show.
 *
 * Captures are still fetched per project — rooms are grouped into one hidden
 * project per site, so that is a handful of requests, each cached under the
 * key the board's own captures already use.
 */
export function useRooms() {
  const roomsQuery = useQuery({
    queryFn: listRooms,
    queryKey: canvasKeys.rooms(),
  });
  const rooms = roomsQuery.data ?? [];

  const projectIds = [...new Set(rooms.map((room) => room.projectId))];
  const captureQueries = useQueries({
    queries: projectIds.map((projectId) => ({
      queryFn: () => listProjectCaptures(projectId),
      queryKey: canvasKeys.projectCaptures(projectId),
      // Captures only change when this client captures something, and that
      // path writes to the cache itself.
      staleTime: Infinity,
    })),
  });
  const captures: CanvasCapture[] = captureQueries.flatMap(
    (query) => query.data ?? [],
  );

  // Each site's panel — a room starts with its site's panel, so these are the
  // faces on its card. Same key as the panel manager, so they share a cache.
  const personaQueries = useQueries({
    queries: projectIds.map((projectId) => ({
      queryFn: () => listPersonas(projectId),
      queryKey: personaKeys.panel(projectId),
    })),
  });
  const panels = new Map<string, Persona[]>(
    projectIds.map((projectId, index) => [
      projectId,
      personaQueries[index]?.data ?? [],
    ]),
  );

  return { captures, loading: roomsQuery.isPending, panels, rooms };
}
